// src/ui/components/spellingReef.ts — Spelling Reef Station
// Integrates best practices from Nessy (Orton-Gillingham structured literacy,
// syllabification "Silly Bull", visual mnemonics, morphology, diagnostic assessment)
// and Toe by Toe (polynon nonsense words, micro-step decoding, error tracking,
// 20-minute sessions, 1-to-1 coaching, immediate feedback loops).
// Aligned with KS2 SPAG curriculum and Bexley 11+ requirements.

import { tokens } from '../designTokens';
import { soundEngine } from '../../audio/soundEngine';
import { SpeechManager } from '../../audio/speechManager';
import { SpellingStorage } from '../../storage/spellingStorage';
import { StationProgression } from '../../storage/stationProgression';
import type { ClaweyHud } from './claweyHud';

// ───────────────────────────────────────────────────
// Types & Data Structures
// ───────────────────────────────────────────────────

/** Difficulty tiers inspired by Nessy's cumulative progression */
export type SpellingTier = 'shallows' | 'reef' | 'deep' | 'abyss';

/** A single spelling word with its Nessy-style strategy annotations */
export interface SpellingWord {
  word: string;
  phonemes: string[];           // Phonemic breakdown (Nessy systematic phonics)
  syllables: string[];          // Syllabification chunks (Nessy "Silly Bull")
  morphemes?: string[];         // Morphological decomposition (prefix/root/suffix)
  mnemonic?: string;            // Visual mnemonic story (Nessy quirky anchors)
  mnemonicEmoji?: string;       // Emoji anchor for the mnemonic
  rule?: string;                // Explicit spelling rule (Nessy strategy-driven)
  tier: SpellingTier;
}

/** Toe by Toe "Polynon" — nonsense word for decoding drill */
export interface Polynon {
  nonsenseWord: string;
  phonemePattern: string;       // The phoneme pattern being tested
  realWordExample: string;      // Real word that uses the same pattern
  tier: SpellingTier;
}

/** Error tracking entry (Toe by Toe immediate feedback loop) */
export interface SpellingAttempt {
  word: string;
  attempt: string;
  correct: boolean;
  timestamp: number;
  errorType?: 'phoneme' | 'syllable' | 'morpheme' | 'polynon';
}

/** Activity modes within the Spelling Reef */
export type SpellingActivity =
  | 'diagnostic'       // Nessy Challenge — initial skill gap diagnostic
  | 'polynon-drill'    // Toe by Toe polynon nonsense word decoding
  | 'syllable-splash'  // Nessy syllabification chunking exercise
  | 'morpheme-mine'    // Nessy morphology (prefixes/suffixes/roots)
  | 'mnemonic-cove'    // Nessy visual mnemonic anchoring
  | 'reef-review';     // Spaced-repetition review of error words

// ───────────────────────────────────────────────────
// Curated Word Banks (KS2 Year 4 Statutory + Marine Theme)
// ───────────────────────────────────────────────────

export const SPELLING_WORDS: SpellingWord[] = [
  // ── Tier 1: Shallows (CVC → CCVC patterns, consonant blends & short vowels) ──
  {
    word: 'crab',
    phonemes: ['c', 'r', 'a', 'b'],
    syllables: ['crab'],
    rule: 'Consonant cluster: two consonants before the vowel (cr-)',
    tier: 'shallows',
  },
  {
    word: 'shell',
    phonemes: ['sh', 'e', 'll'],
    syllables: ['shell'],
    rule: 'Digraph "sh" makes one sound; double "ll" at the end',
    tier: 'shallows',
  },
  {
    word: 'splash',
    phonemes: ['s', 'p', 'l', 'a', 'sh'],
    syllables: ['splash'],
    rule: 'Triple consonant cluster "spl" at the start; digraph "sh" at the end',
    tier: 'shallows',
  },
  {
    word: 'drift',
    phonemes: ['d', 'r', 'i', 'f', 't'],
    syllables: ['drift'],
    rule: 'Initial blend "dr-" + final blend "-ft"',
    tier: 'shallows',
  },
  {
    word: 'shrimp',
    phonemes: ['sh', 'r', 'i', 'm', 'p'],
    syllables: ['shrimp'],
    rule: 'Digraph cluster "shr-" + nasal blend "-mp"',
    tier: 'shallows',
  },
  {
    word: 'cliff',
    phonemes: ['c', 'l', 'i', 'ff'],
    syllables: ['cliff'],
    rule: 'Double "ff" after a short vowel at the end of a one-syllable word',
    tier: 'shallows',
  },
  {
    word: 'plankton',
    phonemes: ['p', 'l', 'a', 'ng', 'k', 't', 'o', 'n'],
    syllables: ['plank', 'ton'],
    rule: 'Two syllables: plank + ton; "nk" blend in the first chunk',
    tier: 'shallows',
  },
  {
    word: 'current',
    phonemes: ['c', 'u', 'rr', 'e', 'n', 't'],
    syllables: ['cur', 'rent'],
    rule: 'Double "rr" splits the two syllables: cur-rent',
    tier: 'shallows',
  },

  // ── Tier 2: Reef (National Curriculum KS2 Year 3 & 4 Statutory Word List) ──
  {
    word: 'different',
    phonemes: ['d', 'i', 'ff', 'er', 'ent'],
    syllables: ['dif', 'fer', 'ent'],
    mnemonic: 'A DIFFerent fish has a diffERENT tail',
    mnemonicEmoji: '🐟',
    rule: 'Double "ff" in the middle; three syllables (dif-fer-ent)',
    tier: 'reef',
  },
  {
    word: 'separate',
    phonemes: ['s', 'e', 'p', 'a', 'r', 'ate'],
    syllables: ['sep', 'a', 'rate'],
    mnemonic: 'There is A RAT in sepARATE',
    mnemonicEmoji: '🐀',
    rule: 'The tricky middle vowel is "a", not "e" (sep-a-rate)',
    tier: 'reef',
  },
  {
    word: 'believe',
    phonemes: ['b', 'e', 'l', 'ie', 've'],
    syllables: ['be', 'lieve'],
    mnemonic: 'Never beLIEve a LIE',
    mnemonicEmoji: '🤥',
    rule: '"i before e except after c" — "ie" in believe',
    tier: 'reef',
  },
  {
    word: 'knowledge',
    phonemes: ['n', 'o', 'l', 'e', 'dge'],
    syllables: ['know', 'ledge'],
    morphemes: ['know', 'ledge'],
    mnemonic: 'You KNOW you can stand on a LEDGE',
    mnemonicEmoji: '🧗',
    rule: 'Silent "k" at the start; "-dge" makes the /j/ sound after short vowel',
    tier: 'reef',
  },
  {
    word: 'temperature',
    phonemes: ['t', 'e', 'm', 'p', 'er', 'a', 'ture'],
    syllables: ['tem', 'per', 'a', 'ture'],
    mnemonic: 'The TEMPER of the ocean changes with TEMPERATURE',
    mnemonicEmoji: '🌡️',
    rule: 'Four syllables: tem-per-a-ture; don\'t skip the middle "a"',
    tier: 'reef',
  },
  {
    word: 'accident',
    phonemes: ['a', 'k', 's', 'i', 'd', 'e', 'n', 't'],
    syllables: ['ac', 'ci', 'dent'],
    mnemonic: 'Two Cats Collide In Danger: AC-CI-DENT',
    mnemonicEmoji: '💥',
    rule: 'Double "cc": first "c" is hard /k/, second "c" before "i" is soft /s/',
    tier: 'reef',
  },
  {
    word: 'caught',
    phonemes: ['c', 'au', 'ght'],
    syllables: ['caught'],
    mnemonic: 'Aunt Ursula Got Huge Trout: A-U-G-H-T',
    mnemonicEmoji: '🎣',
    rule: '"augh" makes the /or/ sound; silent "gh"',
    tier: 'reef',
  },
  {
    word: 'decide',
    phonemes: ['d', 'e', 'c', 'i', 'de'],
    syllables: ['de', 'cide'],
    mnemonic: 'DE-CIDE: the crab decided which shell to choose',
    mnemonicEmoji: '🦀',
    rule: 'Soft "c" followed by "i" sounds like /s/; split digraph i-e',
    tier: 'reef',
  },
  {
    word: 'describe',
    phonemes: ['d', 'e', 's', 'c', 'r', 'i', 'be'],
    syllables: ['de', 'scribe'],
    morphemes: ['de-', 'scribe'],
    mnemonic: 'A SCRIBE writes to DESCRIBE the creatures',
    mnemonicEmoji: '📜',
    rule: 'Root "scribe" (Latin: to write) + split digraph i-e',
    tier: 'reef',
  },
  {
    word: 'early',
    phonemes: ['ear', 'l', 'y'],
    syllables: ['ear', 'ly'],
    mnemonic: 'Your EAR hears the EARLY morning tide',
    mnemonicEmoji: '👂',
    rule: '"ear" makes the /ur/ sound as in early, earth, learn',
    tier: 'reef',
  },
  {
    word: 'exercise',
    phonemes: ['e', 'x', 'er', 'c', 'i', 'se'],
    syllables: ['ex', 'er', 'cise'],
    mnemonic: 'EXtra Energy Requires Crab In Sea: EX-ER-CISE',
    mnemonicEmoji: '🏃',
    rule: '"ex" at the start, soft "c" in the middle, "-se" at the end',
    tier: 'reef',
  },
  {
    word: 'experiment',
    phonemes: ['e', 'x', 'p', 'er', 'i', 'm', 'e', 'n', 't'],
    syllables: ['ex', 'per', 'i', 'ment'],
    morphemes: ['expert', '-ment'],
    mnemonic: 'An EXPERT performs an EXPERIMENT in the ocean lab',
    mnemonicEmoji: '🔬',
    rule: 'Four clean syllables: ex-per-i-ment',
    tier: 'reef',
  },
  {
    word: 'favourite',
    phonemes: ['f', 'a', 'v', 'our', 'i', 'te'],
    syllables: ['fa', 'vour', 'ite'],
    morphemes: ['favour', '-ite'],
    mnemonic: 'OUR favourite lobster has OUR in it: fa-vOUR-ite',
    mnemonicEmoji: '⭐',
    rule: 'British English spelling has "-our-"; three syllables',
    tier: 'reef',
  },
  {
    word: 'guard',
    phonemes: ['g', 'u', 'ar', 'd'],
    syllables: ['guard'],
    mnemonic: 'U Are Armed to GUARD the coral fortress',
    mnemonicEmoji: '🛡️',
    rule: '"gu" before "a" protects the hard /g/ sound; don\'t write "gaurd"!',
    tier: 'reef',
  },
  {
    word: 'island',
    phonemes: ['i', 'l', 'a', 'n', 'd'],
    syllables: ['is', 'land'],
    mnemonic: 'An ISLAND IS LAND surrounded by blue sea',
    mnemonicEmoji: '🏝️',
    rule: 'Silent "s" — it is literally "IS" + "LAND"',
    tier: 'reef',
  },
  {
    word: 'minute',
    phonemes: ['m', 'i', 'n', 'i', 't'],
    syllables: ['min', 'ute'],
    mnemonic: 'A MINUTE crab watched the clock for sixty seconds',
    mnemonicEmoji: '⏱️',
    rule: 'Spelled "min-ute" even though pronounced like /min-it/',
    tier: 'reef',
  },
  {
    word: 'natural',
    phonemes: ['n', 'a', 't', 'ur', 'a', 'l'],
    syllables: ['nat', 'u', 'ral'],
    morphemes: ['nature', '-al'],
    mnemonic: 'A true NATURALIST studies NATURE: NAT-U-RAL',
    mnemonicEmoji: '🌿',
    rule: 'Root "nature" drops "e" before suffix "-al"',
    tier: 'reef',
  },
  {
    word: 'peculiar',
    phonemes: ['p', 'e', 'c', 'u', 'l', 'i', 'ar'],
    syllables: ['pe', 'cu', 'li', 'ar'],
    mnemonic: 'The anglerfish has a PECULIAR light: PE-CU-LI-AR',
    mnemonicEmoji: '🔦',
    rule: 'Four syllables ending in "-ar", not "-er"',
    tier: 'reef',
  },
  {
    word: 'position',
    phonemes: ['p', 'o', 'z', 'i', 'sh', 'u', 'n'],
    syllables: ['po', 'si', 'tion'],
    morphemes: ['posit', '-ion'],
    mnemonic: 'Chart your POSITION on the sonar map: PO-SI-TION',
    mnemonicEmoji: '📍',
    rule: 'Suffix "-tion" makes the /shun/ sound',
    tier: 'reef',
  },
  {
    word: 'possession',
    phonemes: ['p', 'o', 'z', 'e', 'sh', 'u', 'n'],
    syllables: ['pos', 'ses', 'sion'],
    morphemes: ['possess', '-ion'],
    mnemonic: 'To POSSESS something you need 4 S\'s: poSS-eSS-ion',
    mnemonicEmoji: '💎',
    rule: 'Double "ss" in the root + double "ss" before "-ion"',
    tier: 'reef',
  },
  {
    word: 'promise',
    phonemes: ['p', 'r', 'o', 'm', 'i', 'se'],
    syllables: ['prom', 'ise'],
    mnemonic: 'A PRO never forgets a PROMISE',
    mnemonicEmoji: '🤝',
    rule: 'Ends in "-ise", not "-ice" or "-ize"',
    tier: 'reef',
  },
  {
    word: 'quarter',
    phonemes: ['k', 'w', 'or', 't', 'er'],
    syllables: ['quar', 'ter'],
    mnemonic: 'A QUARTER of the compass points North: QUAR-TER',
    mnemonicEmoji: '🧭',
    rule: '"qu" always stick together; "ar" says /or/ after /w/',
    tier: 'reef',
  },
  {
    word: 'remember',
    phonemes: ['r', 'e', 'm', 'e', 'm', 'b', 'er'],
    syllables: ['re', 'mem', 'ber'],
    morphemes: ['re-', 'member'],
    mnemonic: 'RE-MEM-BER: three rhyming-vowel chunks',
    mnemonicEmoji: '🧠',
    rule: 'Three neat syllables with identical "e" vowels',
    tier: 'reef',
  },
  {
    word: 'sentence',
    phonemes: ['s', 'e', 'n', 't', 'e', 'n', 'ce'],
    syllables: ['sen', 'tence'],
    mnemonic: 'TEN crustaceans in a SEN-TENCE',
    mnemonicEmoji: '📝',
    rule: 'Ends in "-ence", not "-ance" or "-ense"',
    tier: 'reef',
  },
  {
    word: 'straight',
    phonemes: ['s', 't', 'r', 'ai', 'ght'],
    syllables: ['straight'],
    mnemonic: 'Swim STRAIGHT ahead: S-T-R + AIGHT',
    mnemonicEmoji: '📏',
    rule: 'Trigraph "aigh" makes long /a/ sound with silent "gh"',
    tier: 'reef',
  },
  {
    word: 'surprise',
    phonemes: ['s', 'er', 'p', 'r', 'i', 'ze'],
    syllables: ['sur', 'prise'],
    mnemonic: 'SURFER gets a SURPRISE: don\'t forget the first "R"!',
    mnemonicEmoji: '🎁',
    rule: 'Prefix "sur-" with an "r"; split digraph i-e with "s"',
    tier: 'reef',
  },
  {
    word: 'thought',
    phonemes: ['th', 'or', 't'],
    syllables: ['thought'],
    mnemonic: 'Tudor Had One Urgent Good Huge Thought: T-H-O-U-G-H-T',
    mnemonicEmoji: '💭',
    rule: '"ought" spelling pattern makes the /or/ sound',
    tier: 'reef',
  },
  {
    word: 'through',
    phonemes: ['th', 'r', 'oo'],
    syllables: ['through'],
    mnemonic: 'Swam THROUGH the kelp: T-H-R-O-U-G-H',
    mnemonicEmoji: '🏊',
    rule: '"ough" makes the /oo/ sound in "through"',
    tier: 'reef',
  },
  {
    word: 'various',
    phonemes: ['v', 'air', 'i', 'u', 's'],
    syllables: ['var', 'i', 'ous'],
    morphemes: ['vary', '-ous'],
    mnemonic: 'VARIOUS marine species: VAR-I-OUS',
    mnemonicEmoji: '🪸',
    rule: 'Root "vary" changes "y" to "i" before suffix "-ous"',
    tier: 'reef',
  },
  {
    word: 'weight',
    phonemes: ['w', 'ai', 't'],
    syllables: ['weight'],
    mnemonic: 'Measuring EIGHT pounds of WEIGHT: W + EIGHT',
    mnemonicEmoji: '⚖️',
    rule: '"eigh" makes the long /a/ sound as in eight and weigh',
    tier: 'reef',
  },

  // ── Tier 3: Deep (Complex morphology & multisyllabic marine biology) ──
  {
    word: 'disappear',
    phonemes: ['d', 'i', 's', 'a', 'pp', 'ear'],
    syllables: ['dis', 'ap', 'pear'],
    morphemes: ['dis-', 'appear'],
    mnemonic: 'DIS makes things vanish — DIS + APPEAR (double P!)',
    mnemonicEmoji: '🫥',
    rule: 'Prefix "dis-" (reverse) + root "appear"; double "p"',
    tier: 'deep',
  },
  {
    word: 'submarine',
    phonemes: ['s', 'u', 'b', 'm', 'a', 'r', 'ine'],
    syllables: ['sub', 'ma', 'rine'],
    morphemes: ['sub-', 'marine'],
    mnemonic: 'SUB means under — a SUBmarine goes under the sea (MARINE)',
    mnemonicEmoji: '🤿',
    rule: 'Prefix "sub-" (under) + Latin root "marine" (sea)',
    tier: 'deep',
  },
  {
    word: 'unfortunately',
    phonemes: ['u', 'n', 'f', 'or', 'tu', 'nate', 'ly'],
    syllables: ['un', 'for', 'tu', 'nate', 'ly'],
    morphemes: ['un-', 'fortune', '-ate', '-ly'],
    mnemonic: 'UN + FORTUNE + ATE + LY — misfortune ate his luck',
    mnemonicEmoji: '🍀',
    rule: 'Prefix "un-" + root "fortune" + suffix "-ate" + suffix "-ly"',
    tier: 'deep',
  },
  {
    word: 'invertebrate',
    phonemes: ['i', 'n', 'v', 'er', 't', 'e', 'b', 'r', 'ate'],
    syllables: ['in', 'ver', 'te', 'brate'],
    morphemes: ['in-', 'vertebrate'],
    mnemonic: 'IN (not) + VERTEBRATE (spine) = crabs and lobsters!',
    mnemonicEmoji: '🦞',
    rule: 'Prefix "in-" means "not" or "without"',
    tier: 'deep',
  },
  {
    word: 'bioluminescence',
    phonemes: ['b', 'i', 'o', 'l', 'u', 'm', 'i', 'n', 'es', 'c', 'ence'],
    syllables: ['bi', 'o', 'lu', 'mi', 'nes', 'cence'],
    morphemes: ['bio-', 'lumin', '-escence'],
    mnemonic: 'BIO (life) + LUMIN (light) + ESCENCE (becoming)',
    mnemonicEmoji: '✨',
    rule: 'Greek "bio" + Latin "lumen" (light) + "-escence"',
    tier: 'deep',
  },
  {
    word: 'exoskeleton',
    phonemes: ['e', 'x', 'o', 's', 'k', 'e', 'l', 'e', 't', 'o', 'n'],
    syllables: ['ex', 'o', 'skel', 'e', 'ton'],
    morphemes: ['exo-', 'skeleton'],
    mnemonic: 'EXO means OUTSIDE: an outer armor skeleton',
    mnemonicEmoji: '🛡️',
    rule: 'Greek prefix "exo-" (outside) + root "skeleton"',
    tier: 'deep',
  },
  {
    word: 'carnivorous',
    phonemes: ['c', 'ar', 'n', 'i', 'v', 'or', 'u', 's'],
    syllables: ['car', 'niv', 'o', 'rous'],
    morphemes: ['carn-', '-vorous'],
    mnemonic: 'CARN (meat) + VOROUS (eating): meat-eating sea predators',
    mnemonicEmoji: '🦈',
    rule: 'Latin root "carn" (flesh) + suffix "-vorous" (eating)',
    tier: 'deep',
  },
  {
    word: 'photosynthesis',
    phonemes: ['f', 'o', 't', 'o', 's', 'i', 'n', 'th', 'e', 's', 'i', 's'],
    syllables: ['pho', 'to', 'syn', 'the', 'sis'],
    morphemes: ['photo-', 'synthesis'],
    mnemonic: 'PHOTO (light) + SYNTHESIS (putting together)',
    mnemonicEmoji: '☀️',
    rule: '"ph" makes /f/; Greek roots photo + synthesis',
    tier: 'deep',
  },

  // ── Tier 4: Abyss (Bexley 11+ Challenge Words) ──
  {
    word: 'environment',
    phonemes: ['e', 'n', 'v', 'i', 'r', 'on', 'm', 'ent'],
    syllables: ['en', 'vi', 'ron', 'ment'],
    morphemes: ['environ', '-ment'],
    mnemonic: 'IRON is in the envIRONment',
    mnemonicEmoji: '⚙️',
    rule: 'Don\'t forget the "n" before "-ment" (en-vi-ron-ment)',
    tier: 'abyss',
  },
  {
    word: 'conscience',
    phonemes: ['c', 'on', 'sc', 'ie', 'nce'],
    syllables: ['con', 'sci', 'ence'],
    morphemes: ['con-', 'science'],
    mnemonic: 'CON + SCIENCE = knowing right from wrong in your mind',
    mnemonicEmoji: '🧠',
    rule: 'Latin root "sci" = to know; "sc" makes the /sh/ sound before "ie"',
    tier: 'abyss',
  },
  {
    word: 'accommodate',
    phonemes: ['a', 'k', 'o', 'm', 'o', 'd', 'ate'],
    syllables: ['ac', 'com', 'mo', 'date'],
    morphemes: ['ad-', 'com-', 'modus'],
    mnemonic: 'Room for 2 Cats and 2 Mice: aCCoMModate (2 C\'s, 2 M\'s)',
    mnemonicEmoji: '🏨',
    rule: 'Double "cc" AND double "mm"',
    tier: 'abyss',
  },
  {
    word: 'necessary',
    phonemes: ['n', 'e', 's', 'e', 's', 'er', 'ee'],
    syllables: ['nec', 'es', 'sar', 'y'],
    mnemonic: 'One Collar, Two Sleeves: 1 C, 2 S\'s in neCeSSary',
    mnemonicEmoji: '👔',
    rule: 'One "c", followed by double "ss"',
    tier: 'abyss',
  },
  {
    word: 'parliament',
    phonemes: ['p', 'ar', 'l', 'a', 'm', 'e', 'n', 't'],
    syllables: ['par', 'lia', 'ment'],
    mnemonic: 'I AM in par-LIA-ment: don\'t forget the "i"!',
    mnemonicEmoji: '🏛️',
    rule: 'From French "parler" (to speak) + "-ment"',
    tier: 'abyss',
  },
  {
    word: 'exaggerate',
    phonemes: ['e', 'g', 'z', 'a', 'j', 'er', 'ate'],
    syllables: ['ex', 'ag', 'ger', 'ate'],
    mnemonic: 'Double G makes the fish story HUGE: EX-AG-GER-ATE',
    mnemonicEmoji: '🗣️',
    rule: 'Single "x", double "gg"',
    tier: 'abyss',
  },
  {
    word: 'embarrass',
    phonemes: ['e', 'm', 'b', 'a', 'r', 'a', 's'],
    syllables: ['em', 'bar', 'rass'],
    mnemonic: 'Really Red, Super Shy: 2 R\'s, 2 S\'s in embaRRaSS',
    mnemonicEmoji: '😳',
    rule: 'Double "rr" AND double "ss"',
    tier: 'abyss',
  },
  {
    word: 'privilege',
    phonemes: ['p', 'r', 'i', 'v', 'i', 'l', 'e', 'dge'],
    syllables: ['priv', 'i', 'lege'],
    mnemonic: 'No "D" in PRIVILEGE: 2 I\'s and 2 E\'s',
    mnemonicEmoji: '👑',
    rule: 'Ends in "-lege", not "-ledge" or "-lige"',
    tier: 'abyss',
  },
];

/** Toe by Toe Polynon (nonsense word) bank for pure decoding */
export const POLYNONS: Polynon[] = [
  // Shallows
  { nonsenseWord: 'brell', phonemePattern: 'CCVCC (br-e-ll)', realWordExample: 'shell', tier: 'shallows' },
  { nonsenseWord: 'snarp', phonemePattern: 'CCVCC (sn-a-rp)', realWordExample: 'sharp', tier: 'shallows' },
  { nonsenseWord: 'chund', phonemePattern: 'CVCC (ch-u-nd)', realWordExample: 'chunk', tier: 'shallows' },
  { nonsenseWord: 'ploft', phonemePattern: 'CCVCC (pl-o-ft)', realWordExample: 'loft', tier: 'shallows' },
  { nonsenseWord: 'gralm', phonemePattern: 'CCVCC (gr-a-lm)', realWordExample: 'calm', tier: 'shallows' },
  { nonsenseWord: 'frest', phonemePattern: 'CCVCC (fr-e-st)', realWordExample: 'frost', tier: 'shallows' },
  { nonsenseWord: 'drimp', phonemePattern: 'CCVCC (dr-i-mp)', realWordExample: 'shrimp', tier: 'shallows' },
  { nonsenseWord: 'blenk', phonemePattern: 'CCVCC (bl-e-nk)', realWordExample: 'plank', tier: 'shallows' },

  // Reef
  { nonsenseWord: 'trepnol', phonemePattern: 'CCVC-CVC', realWordExample: 'control', tier: 'reef' },
  { nonsenseWord: 'blimtock', phonemePattern: 'CCVC-CVCC', realWordExample: 'hemlock', tier: 'reef' },
  { nonsenseWord: 'spanchet', phonemePattern: 'CCVC-CVC', realWordExample: 'blanket', tier: 'reef' },
  { nonsenseWord: 'flindor', phonemePattern: 'CCVC-CVC', realWordExample: 'splendour', tier: 'reef' },
  { nonsenseWord: 'kroptin', phonemePattern: 'CCVC-CVC', realWordExample: 'captain', tier: 'reef' },
  { nonsenseWord: 'drantel', phonemePattern: 'CCVC-CVC', realWordExample: 'mantle', tier: 'reef' },

  // Deep
  { nonsenseWord: 'crambulate', phonemePattern: 'CCVC-CVC-VCC', realWordExample: 'calculate', tier: 'deep' },
  { nonsenseWord: 'disfrontment', phonemePattern: 'CVC-CCVCC-CVCC', realWordExample: 'displacement', tier: 'deep' },
  { nonsenseWord: 'subventral', phonemePattern: 'CVC-CVC-CCVC', realWordExample: 'submarine', tier: 'deep' },
  { nonsenseWord: 'interplink', phonemePattern: 'VC-CVC-CCCVCC', realWordExample: 'interlink', tier: 'deep' },

  // Abyss
  { nonsenseWord: 'prethundicate', phonemePattern: 'CCV-CCVC-CVC-VCC', realWordExample: 'communicate', tier: 'abyss' },
  { nonsenseWord: 'circumstront', phonemePattern: 'CVC-CVC-CCCVCC', realWordExample: 'circumnavigate', tier: 'abyss' },
  { nonsenseWord: 'antifluxion', phonemePattern: 'VC-CVC-CCV-CVC', realWordExample: 'connection', tier: 'abyss' },
];

// ───────────────────────────────────────────────────
// Session Timer (Toe by Toe: strict 20-minute rule)
// ───────────────────────────────────────────────────

const SESSION_DURATION_MS = 20 * 60 * 1000; // 20 minutes

// ───────────────────────────────────────────────────
// Spelling Reef Component
// ───────────────────────────────────────────────────

export class SpellingReef {
  private currentActivity: SpellingActivity = 'diagnostic';
  private currentTier: SpellingTier = 'shallows';
  private currentWordIndex = 0;
  private currentPolynon = 0;
  private errorLog: SpellingAttempt[] = [];
  private sessionStartTime = 0;
  private sessionTimerInterval: ReturnType<typeof setInterval> | null = null;
  private sessionActive = false;
  private streakCount = 0;
  private bestStreak = 0;
  private hud?: ClaweyHud;

  constructor(hud?: ClaweyHud) {
    this.hud = hud;

    // Load persisted progress from local storage
    const saved = SpellingStorage.load();
    this.errorLog = saved.errorLog || [];
    this.streakCount = saved.streakCount || 0;
    this.bestStreak = saved.bestStreak || 0;
    if (saved.currentTier) this.currentTier = saved.currentTier;
    if (saved.currentActivity) this.currentActivity = saved.currentActivity;
  }

  setHud(hud: ClaweyHud): void {
    this.hud = hud;
  }

  /** Render the complete Spelling Reef Station */
  render(): HTMLElement {
    const container = document.createElement('div');
    container.id = 'spelling-reef-station';

    container.innerHTML = `
      ${this.renderHeader()}
      ${this.renderSessionTimer()}
      ${this.renderActivitySelector()}
      <div id="spelling-activity-stage">
        ${this.renderCurrentActivity()}
      </div>
      ${this.renderProgressTracker()}
      ${this.renderMethodologyInfo()}
    `;

    // Wire up static controls once (sound toggle, activity selector, session timer)
    this.attachStaticListeners(container);

    // Wire up stage controls for active activity
    this.attachStageListeners(container);

    return container;
  }

  // ─── Header ──────────────────────────────────────

  private renderHeader(): string {
    const isMuted = soundEngine.isMuted();

    return `
      <section class="morris-card morris-card-gold" style="margin-bottom: 24px; padding: 28px 24px; position: relative; overflow: hidden;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 18px;">
          <div style="flex: 1; min-width: 280px;">
            <div style="display: inline-flex; align-items: center; gap: 8px; padding: 6px 14px; border: 1px solid ${tokens.color.gildedBrass}; border-radius: 20px; font-size: 0.85rem; color: ${tokens.color.madderCoral}; font-weight: 800; text-transform: uppercase; margin-bottom: 14px; background: rgba(255, 246, 229, 0.6);">
              <span style="font-size: 1rem;">🐚</span> Coral Spelling Reef
            </div>
            <h2 style="font-size: 2.1rem; margin-bottom: 10px; color: ${tokens.color.morrisIndigo};">
              The Spelling Reef
            </h2>
            <p style="max-width: 580px; font-size: 1.1rem; color: ${tokens.color.morrisIndigo}; margin-bottom: 6px; line-height: 1.7;">
              Master spelling with <strong>multisensory decoding</strong>, syllable chunking, 
              visual mnemonics, and nonsense-word drills. Inspired by Orton-Gillingham & Toe by Toe.
            </p>
            <div style="display: flex; gap: 8px; flex-wrap: wrap; margin-top: 12px;">
              <span style="font-size: 0.78rem; padding: 4px 10px; border-radius: 8px; background: rgba(61, 90, 69, 0.12); color: ${tokens.color.seaweedSage}; font-weight: 700; border: 1px solid rgba(61, 90, 69, 0.2);">
                Orton-Gillingham Phonics
              </span>
              <span style="font-size: 0.78rem; padding: 4px 10px; border-radius: 8px; background: rgba(255, 107, 74, 0.1); color: ${tokens.color.madderCoral}; font-weight: 700; border: 1px solid rgba(255, 107, 74, 0.2);">
                Toe by Toe Polynons
              </span>
              <span style="font-size: 0.78rem; padding: 4px 10px; border-radius: 8px; background: rgba(26, 54, 93, 0.1); color: ${tokens.color.morrisIndigo}; font-weight: 700; border: 1px solid rgba(26, 54, 93, 0.15);">
                KS2 Year 4 Statutory
              </span>
            </div>
          </div>

          <!-- Streak, Tier Display & Audio Toggle -->
          <div style="display: flex; flex-direction: column; align-items: center; gap: 10px; min-width: 150px;">
            <div style="text-align: center; padding: 14px 20px; background: rgba(255, 246, 229, 0.85); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 12px; width: 100%;">
              <div style="font-size: 0.75rem; color: ${tokens.color.antiqueBrass}; font-weight: 800; text-transform: uppercase;">Current Streak</div>
              <div id="spelling-streak" style="font-family: ${tokens.font.mono}; font-size: 2.4rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; line-height: 1.2;">${this.streakCount}</div>
              <div style="font-size: 0.7rem; color: ${tokens.color.seaweedSage}; font-weight: 700;">Best: ${this.bestStreak}</div>
            </div>
            <div style="display: flex; gap: 8px; width: 100%; justify-content: center;">
              <div style="font-size: 0.8rem; padding: 6px 12px; border-radius: 20px; background: ${this.getTierColor()}; color: #fff; font-weight: 800;">
                ${this.getTierLabel()}
              </div>
              <button id="btn-toggle-sound" class="touch-target" style="padding: 6px 12px; border-radius: 20px; font-size: 0.8rem; font-weight: 800; background: rgba(26, 54, 93, 0.08); border: 1px solid rgba(26, 54, 93, 0.2); color: ${tokens.color.morrisIndigo}; cursor: pointer;">
                ${isMuted ? '🔇 Off' : '🔊 Sound'}
              </button>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  // ─── Session Timer (Toe by Toe: 20-minute rule) ──

  private renderSessionTimer(): string {
    return `
      <div id="session-timer-bar" class="morris-card" style="margin-bottom: 20px; padding: 14px 20px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px;">
        <div style="display: flex; align-items: center; gap: 12px;">
          <span style="font-size: 1.3rem;">⏱️</span>
          <div>
            <div style="font-size: 0.78rem; color: ${tokens.color.antiqueBrass}; font-weight: 800; text-transform: uppercase;">Session Timer</div>
            <div style="font-size: 0.85rem; color: ${tokens.color.morrisIndigo};">
              Short, focused 20-minute sessions for best results
            </div>
          </div>
        </div>
        <div style="display: flex; align-items: center; gap: 14px;">
          <div style="background: rgba(10, 25, 47, 0.06); padding: 6px 14px; border-radius: 8px; font-family: ${tokens.font.mono}; font-size: 1.25rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">
            <span id="timer-display">20:00</span>
          </div>
          <button id="btn-start-session" class="touch-target btn-gold" style="padding: 8px 18px; font-size: 0.85rem;">
            ▶ Start 20-Min
          </button>
        </div>
        <div style="width: 100%; height: 5px; background: rgba(26, 54, 93, 0.08); border-radius: 3px; overflow: hidden; margin-top: 4px;">
          <div id="timer-progress" style="width: 0%; height: 100%; background: ${tokens.color.madderCoral}; transition: width 1s linear;"></div>
        </div>
      </div>
    `;
  }

  // ─── Activity Selector ───────────────────────────

  private renderActivitySelector(): string {
    const activities: { id: SpellingActivity; name: string; subtitle: string; image: string; glowColor: string }[] = [
      { id: 'diagnostic', name: 'Reef Diagnostic', subtitle: 'Skill Gap Check', image: '/assets/spelling/spelling-card-diagnostic.png', glowColor: '#FF6B4A' },
      { id: 'polynon-drill', name: 'Polynon Drill', subtitle: 'Toe by Toe Decoding', image: '/assets/spelling/spelling-card-polynon.png', glowColor: '#F59E0B' },
      { id: 'syllable-splash', name: 'Syllable Splash', subtitle: 'Chunk the Words', image: '/assets/spelling/spelling-card-syllables.png', glowColor: '#00E5FF' },
      { id: 'morpheme-mine', name: 'Morpheme Mine', subtitle: 'Prefix & Suffixes', image: '/assets/spelling/spelling-card-morpheme.png', glowColor: '#A855F7' },
      { id: 'mnemonic-cove', name: 'Mnemonic Cove', subtitle: 'Visual Memory Tricks', image: '/assets/spelling/spelling-card-mnemonic.png', glowColor: '#EC4899' },
      { id: 'reef-review', name: 'Reef Review', subtitle: 'Error Word Practice', image: '/assets/spelling/spelling-card-review.png', glowColor: '#06B6D4' },
    ];

    return `
      <div class="spelling-cards-grid" style="margin-bottom: 26px;">
        ${activities.map(a => {
          const isActive = this.currentActivity === a.id;
          return `
            <button
              class="touch-target spelling-activity-btn spelling-card-btn ${isActive ? 'active-activity' : ''}"
              data-activity="${a.id}"
              style="--spell-glow: ${a.glowColor};"
              aria-label="${a.name}: ${a.subtitle}"
            >
              <div class="spelling-card-inner">
                <img 
                  src="${a.image}" 
                  alt="${a.name}" 
                  class="spelling-card-img"
                  loading="lazy"
                />
                <div class="spelling-card-glow-overlay"></div>
                <div class="spelling-card-caption">
                  <span class="spelling-card-title">${a.name}</span>
                  <span class="spelling-card-sub">${a.subtitle}</span>
                </div>
              </div>
            </button>
          `;
        }).join('')}
      </div>
    `;
  }

  // ─── Activity Stages ─────────────────────────────

  private renderCurrentActivity(): string {
    switch (this.currentActivity) {
      case 'diagnostic': return this.renderDiagnostic();
      case 'polynon-drill': return this.renderPolynons();
      case 'syllable-splash': return this.renderSyllableSplash();
      case 'morpheme-mine': return this.renderMorphemeMine();
      case 'mnemonic-cove': return this.renderMnemonicCove();
      case 'reef-review': return this.renderReefReview();
    }
  }

  /** Nessy Challenge — Diagnostic word-by-word skill gap assessment */
  private renderDiagnostic(): string {
    return `
      <section class="morris-card" style="padding: 28px 24px;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 18px; flex-wrap: wrap; gap: 10px;">
          <div>
            <h3 style="margin-bottom: 4px;">🔍 Reef Diagnostic Challenge</h3>
            <p style="font-size: 0.9rem; color: #6B7C93;">
              Spell each word accurately. We'll identify exact patterns needing practice.
            </p>
          </div>
          <span style="font-size: 0.8rem; padding: 5px 12px; border-radius: 8px; background: rgba(0, 229, 255, 0.1); color: ${tokens.color.morrisIndigo}; font-weight: 800;">
            Word ${this.currentWordIndex + 1} of ${SPELLING_WORDS.length}
          </span>
        </div>

        <!-- Audio prompt: pleasant human voice engine & hear word button -->
        <div style="background: rgba(250, 246, 237, 0.85); border-radius: 16px; padding: 22px 20px; text-align: center; margin-bottom: 20px; border: 1.5px solid rgba(229, 169, 60, 0.25); box-shadow: 0 2px 8px rgba(10, 25, 47, 0.05);">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; flex-wrap: wrap; gap: 10px;">
            
            <!-- Voice Selector -->
            <div style="display: flex; align-items: center; gap: 8px;">
              <label for="select-active-voice" style="font-size: 0.82rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; display: flex; align-items: center; gap: 4px;">
                <span>🌸</span> Voice:
              </label>
              <select id="select-active-voice" style="padding: 6px 12px; border-radius: 8px; border: 1.5px solid rgba(26,54,93,0.25); font-size: 0.82rem; font-weight: 700; background: #FFFDF9; color: ${tokens.color.morrisIndigo}; cursor: pointer; max-width: 280px;">
                <option value="">Detecting natural voices...</option>
              </select>
            </div>

            <!-- Speed Selector -->
            <div style="display: flex; align-items: center; gap: 6px;">
              <label for="select-voice-speed" style="font-size: 0.78rem; font-weight: 700; color: ${tokens.color.morrisIndigo};">Speed:</label>
              <select id="select-voice-speed" style="padding: 6px 10px; border-radius: 8px; border: 1.5px solid rgba(26,54,93,0.25); font-size: 0.78rem; font-weight: 700; background: #FFFDF9; color: ${tokens.color.morrisIndigo}; cursor: pointer;">
                <option value="0.85" ${Math.abs(SpeechManager.getSpeedMultiplier() - 0.85) < 0.03 ? 'selected' : ''}>0.85x (Gentle Pace)</option>
                <option value="0.92" ${Math.abs(SpeechManager.getSpeedMultiplier() - 0.92) < 0.03 ? 'selected' : ''}>0.92x (Pleasant Natural)</option>
                <option value="1.0" ${Math.abs(SpeechManager.getSpeedMultiplier() - 1.0) < 0.03 ? 'selected' : ''}>1.0x (Standard)</option>
              </select>
            </div>
          </div>

          <!-- Hear Word & Test Voice Action Bar -->
          <div style="display: flex; justify-content: center; gap: 12px; align-items: center; flex-wrap: wrap; margin-bottom: 10px;">
            <button id="btn-hear-word" class="touch-target btn-primary" style="padding: 14px 28px; font-size: 1.15rem; font-weight: 800; display: inline-flex; align-items: center; gap: 8px;">
              <span>🔊</span> Hear the Word
            </button>
            <button id="btn-test-voice" type="button" class="touch-target" style="padding: 10px 18px; font-size: 0.85rem; font-weight: 700; border: 1.5px solid rgba(26,54,93,0.3); border-radius: 12px; background: #FFFDF9; color: ${tokens.color.morrisIndigo}; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;">
              <span>▶</span> Test Voice
            </button>
          </div>

          <div id="voice-persona-desc" style="font-size: 0.8rem; color: #5A6D86; font-style: italic;">
            🌸 <strong>Pleasant British Woman:</strong> Clear, natural human pronunciation calibrated for KS2 phonics practice.
          </div>
        </div>

        <!-- Spelling input field -->
        <div style="margin-bottom: 18px;">
          <label style="display: block; font-size: 0.85rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; margin-bottom: 8px;">
            Type the word here:
          </label>
          <div style="display: flex; gap: 12px; flex-wrap: wrap;">
            <input
              type="text"
              id="spelling-input"
              autocomplete="off"
              spellcheck="false"
              placeholder="Type here..."
              style="
                flex: 1; min-width: 200px; font-family: ${tokens.font.mono}; font-size: 1.4rem;
                padding: 14px 18px; border: 2px solid rgba(26, 54, 93, 0.2); border-radius: 12px;
                background: rgba(255, 253, 249, 0.95); color: ${tokens.color.morrisIndigo};
                letter-spacing: 0.15em; text-align: center; outline: none;
                transition: border-color 0.2s ease, box-shadow 0.2s ease;
              "
            />
            <button id="btn-check-spelling" class="touch-target btn-primary" style="padding: 14px 28px;">
              ✓ Check
            </button>
          </div>
        </div>

        <!-- Feedback area -->
        <div id="spelling-feedback" style="min-height: 80px; padding: 16px; border-radius: 12px; background: rgba(250, 246, 237, 0.6); display: none;">
        </div>

        <!-- Navigation -->
        <div style="display: flex; justify-content: space-between; margin-top: 18px;">
          <button id="btn-prev-word" class="touch-target" style="padding: 10px 20px;" ${this.currentWordIndex === 0 ? 'disabled style="opacity:0.4; padding: 10px 20px;"' : ''}>
            ← Previous
          </button>
          <button id="btn-next-word" class="touch-target btn-gold" style="padding: 10px 20px;">
            Next Word →
          </button>
        </div>
      </section>

      <!-- Diagnostic Key (Nessy: explicit strategy teaching) -->
      <div class="morris-card" style="margin-top: 16px; padding: 18px 20px;">
        <h4 style="margin-bottom: 10px; font-size: 1rem; color: ${tokens.color.morrisIndigo};">
          📋 Clawey's Diagnostic Log (${SPELLING_WORDS.length} Curated Words)
        </h4>
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 8px;">
          ${this.renderDiagnosticWordGrid()}
        </div>
      </div>
    `;
  }

  /** Toe by Toe Polynon (nonsense word) drill — eliminates guessing */
  private renderPolynons(): string {
    const tierPolynons = POLYNONS.filter(p => p.tier === this.currentTier);
    const polynon = tierPolynons[this.currentPolynon % (tierPolynons.length || 1)];

    if (!polynon) {
      return `<section class="morris-card" style="padding: 28px; text-align: center;">
        <p>No polynons available for this tier. Try a different depth!</p>
      </section>`;
    }

    return `
      <section class="morris-card" style="padding: 28px 24px;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 18px; flex-wrap: wrap; gap: 10px;">
          <div>
            <h3 style="margin-bottom: 4px;">🐡 Polynon Decoding Drill</h3>
            <p style="font-size: 0.9rem; color: #6B7C93;">
              <strong>No guessing allowed!</strong> Sound out each nonsense word to train pure phoneme decoding.
            </p>
          </div>
          <span style="font-size: 0.8rem; padding: 5px 12px; border-radius: 8px; background: rgba(255, 107, 74, 0.1); color: ${tokens.color.madderCoral}; font-weight: 800;">
            Polynon ${this.currentPolynon + 1} of ${tierPolynons.length}
          </span>
        </div>

        <!-- The Polynon Display (large, clear, decodable) -->
        <div style="background: linear-gradient(135deg, rgba(10, 25, 47, 0.92), rgba(26, 54, 93, 0.95)); border-radius: 16px; padding: 40px 24px; text-align: center; margin-bottom: 20px; position: relative; overflow: hidden;">
          <div style="position: absolute; inset: 0; background: radial-gradient(circle at 70% 30%, rgba(0, 229, 255, 0.08), transparent 60%); pointer-events: none;"></div>
          <div style="font-size: 0.75rem; color: rgba(255,255,255,0.5); text-transform: uppercase; font-weight: 800; letter-spacing: 0.1em; margin-bottom: 8px;">
            Nonsense Word — Sound It Out
          </div>
          <div id="polynon-display" style="font-family: ${tokens.font.mono}; font-size: 3.2rem; font-weight: 800; color: #FFFFFF; letter-spacing: 0.18em; text-shadow: 0 0 20px rgba(0, 229, 255, 0.4); margin-bottom: 12px;">
            ${polynon.nonsenseWord}
          </div>
          <div style="font-size: 0.85rem; color: rgba(255,255,255,0.6);">
            Pattern: <span style="color: ${tokens.color.biolumCyan}; font-weight: 700;">${polynon.phonemePattern}</span>
          </div>
        </div>

        <!-- Decoding Response -->
        <div style="margin-bottom: 18px;">
          <label style="display: block; font-size: 0.85rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; margin-bottom: 8px;">
            Type what you sounded out:
          </label>
          <div style="display: flex; gap: 12px; flex-wrap: wrap;">
            <input
              type="text"
              id="polynon-input"
              autocomplete="off"
              spellcheck="false"
              placeholder="Type the nonsense word..."
              style="
                flex: 1; min-width: 200px; font-family: ${tokens.font.mono}; font-size: 1.4rem;
                padding: 14px 18px; border: 2px solid rgba(26, 54, 93, 0.2); border-radius: 12px;
                background: rgba(255, 253, 249, 0.95); color: ${tokens.color.morrisIndigo};
                letter-spacing: 0.15em; text-align: center; outline: none;
              "
            />
            <button id="btn-check-polynon" class="touch-target btn-primary" style="padding: 14px 28px;">
              ✓ Decode
            </button>
          </div>
        </div>

        <div id="polynon-feedback" style="min-height: 60px; padding: 16px; border-radius: 12px; background: rgba(250, 246, 237, 0.6); display: none;"></div>

        <!-- Real-word connection (bridging polynon to real vocabulary) -->
        <div id="polynon-bridge" style="display: none; margin-top: 16px; padding: 16px; border-radius: 12px; background: rgba(0, 229, 255, 0.06); border: 1px solid rgba(0, 229, 255, 0.15);">
          <p style="font-size: 0.9rem; color: ${tokens.color.morrisIndigo};">
            <strong>🌉 Real Word Bridge:</strong> The same phonemic pattern appears in
            <span style="font-family: ${tokens.font.mono}; font-weight: 800; color: ${tokens.color.madderCoral};">"${polynon.realWordExample}"</span>
          </p>
        </div>

        <!-- Navigation -->
        <div style="display: flex; justify-content: space-between; margin-top: 18px;">
          <button id="btn-prev-polynon" class="touch-target" style="padding: 10px 20px;" ${this.currentPolynon === 0 ? 'disabled style="opacity:0.4; padding: 10px 20px;"' : ''}>
            ← Previous
          </button>
          <button id="btn-next-polynon" class="touch-target btn-gold" style="padding: 10px 20px;">
            Next Polynon →
          </button>
        </div>
      </section>
    `;
  }

  /** Nessy "Silly Bull" syllable chunking exercise */
  private renderSyllableSplash(): string {
    const multisyllabicWords = SPELLING_WORDS.filter(w => w.syllables.length >= 2);
    const word = multisyllabicWords[this.currentWordIndex % multisyllabicWords.length];

    if (!word) {
      return `<section class="morris-card" style="padding: 28px; text-align: center;">
        <p>No multi-syllable words available. Add more to the word bank!</p>
      </section>`;
    }

    return `
      <section class="morris-card" style="padding: 28px 24px;">
        <div style="margin-bottom: 18px;">
          <h3 style="margin-bottom: 4px;">🌊 Syllable Splash</h3>
          <p style="font-size: 0.9rem; color: #6B7C93;">
            Break each word into <strong>syllable chunks</strong>. Clap them out, then type each chunk!
          </p>
        </div>

        <!-- Word display with syllable slots -->
        <div style="background: rgba(250, 246, 237, 0.85); border-radius: 16px; padding: 32px 20px; text-align: center; margin-bottom: 20px; border: 1.5px solid rgba(229, 169, 60, 0.2);">
          <div style="font-size: 0.75rem; color: ${tokens.color.antiqueBrass}; font-weight: 800; text-transform: uppercase; margin-bottom: 12px;">
            How many syllables? Split the word!
          </div>
          <div style="font-family: ${tokens.font.heading}; font-size: 2.6rem; font-weight: 700; color: ${tokens.color.morrisIndigo}; letter-spacing: 0.06em; margin-bottom: 18px;">
            ${word.word}
          </div>

          <!-- Syllable chunking input: slots for each syllable -->
          <div id="syllable-slots" style="display: flex; justify-content: center; gap: 8px; flex-wrap: wrap; margin-bottom: 16px;">
            ${word.syllables.map((_s, i) => `
              <input
                type="text"
                class="syllable-slot"
                data-index="${i}"
                autocomplete="off"
                spellcheck="false"
                placeholder="chunk ${i + 1}"
                style="
                  width: ${Math.max(80, 28 * word.syllables[i]!.length)}px;
                  font-family: ${tokens.font.mono}; font-size: 1.3rem;
                  padding: 12px 8px; border: 2px dashed rgba(26, 54, 93, 0.25);
                  border-radius: 10px; background: #fff; color: ${tokens.color.morrisIndigo};
                  text-align: center; outline: none;
                  transition: border-color 0.2s, background 0.2s;
                "
              />
            `).join('<span style="font-size: 1.5rem; color: rgba(26, 54, 93, 0.3); align-self: center;">·</span>')}
          </div>

          <button id="btn-check-syllables" class="touch-target btn-primary" style="padding: 12px 28px;">
            ✓ Check My Chunks
          </button>
        </div>

        <!-- Syllable feedback -->
        <div id="syllable-feedback" style="min-height: 60px; display: none;"></div>

        <!-- Syllable rule card -->
        ${word.rule ? `
          <div id="syllable-rule" style="display: none; margin-top: 14px; padding: 16px 18px; border-radius: 12px; background: rgba(61, 90, 69, 0.08); border: 1px solid rgba(61, 90, 69, 0.15);">
            <p style="font-size: 0.9rem; color: ${tokens.color.morrisIndigo};">
              <strong>📘 Spelling Rule:</strong> ${word.rule}
            </p>
          </div>
        ` : ''}

        <div style="display: flex; justify-content: flex-end; margin-top: 16px;">
          <button id="btn-next-syllable" class="touch-target btn-gold" style="padding: 10px 20px;">
            Next Word →
          </button>
        </div>
      </section>
    `;
  }

  /** Nessy morphology exercise (prefixes, roots, suffixes) */
  private renderMorphemeMine(): string {
    const morphWords = SPELLING_WORDS.filter(w => w.morphemes && w.morphemes.length >= 2);
    const word = morphWords[this.currentWordIndex % morphWords.length];

    if (!word || !word.morphemes) {
      return `<section class="morris-card" style="padding: 28px; text-align: center;">
        <p>Dive deeper to find words with prefixes and suffixes!</p>
      </section>`;
    }

    return `
      <section class="morris-card" style="padding: 28px 24px;">
        <div style="margin-bottom: 18px;">
          <h3 style="margin-bottom: 4px;">⛏️ Morpheme Mine</h3>
          <p style="font-size: 0.9rem; color: #6B7C93;">
            Unearth the building blocks: <strong>prefixes</strong>, <strong>roots</strong>, and <strong>suffixes</strong>.
          </p>
        </div>

        <!-- Morpheme breakdown blocks -->
        <div style="background: rgba(250, 246, 237, 0.85); border-radius: 16px; padding: 32px 20px; text-align: center; margin-bottom: 20px; border: 1.5px solid rgba(229, 169, 60, 0.2);">
          <div style="font-size: 0.75rem; color: ${tokens.color.antiqueBrass}; font-weight: 800; text-transform: uppercase; margin-bottom: 14px;">
            Word Architecture
          </div>

          <div style="display: flex; justify-content: center; gap: 10px; flex-wrap: wrap; margin-bottom: 20px;">
            ${word.morphemes.map((m) => {
              const isPrefix = m.endsWith('-');
              const isSuffix = m.startsWith('-');
              const role = isPrefix ? 'Prefix' : isSuffix ? 'Suffix' : 'Root';
              const color = isPrefix ? tokens.color.madderCoral : isSuffix ? tokens.color.seaweedSage : tokens.color.morrisIndigo;
              const bg = isPrefix ? 'rgba(255, 107, 74, 0.1)' : isSuffix ? 'rgba(61, 90, 69, 0.1)' : 'rgba(26, 54, 93, 0.08)';

              return `
                <div style="padding: 14px 18px; border-radius: 12px; background: ${bg}; border: 1.5px solid ${color}; min-width: 90px; text-align: center;">
                  <div style="font-size: 0.7rem; color: ${color}; font-weight: 800; text-transform: uppercase; margin-bottom: 4px;">
                    ${role}
                  </div>
                  <div style="font-family: ${tokens.font.mono}; font-size: 1.4rem; font-weight: 800; color: ${color};">
                    ${m}
                  </div>
                </div>
              `;
            }).join('<span style="font-size: 1.8rem; color: ${tokens.color.antiqueBrass}; align-self: center;">+</span>')}
          </div>

          <div style="font-size: 0.9rem; color: ${tokens.color.morrisIndigo}; line-height: 1.6; max-width: 500px; margin: 0 auto;">
            ${word.rule || 'Assemble the parts to construct the complete statutory word.'}
          </div>
        </div>

        <!-- Assembly check -->
        <div style="margin-bottom: 18px;">
          <label style="display: block; font-size: 0.85rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; margin-bottom: 8px;">
            Assemble the full word:
          </label>
          <div style="display: flex; gap: 12px; flex-wrap: wrap;">
            <input
              type="text"
              id="morpheme-input"
              autocomplete="off"
              spellcheck="false"
              placeholder="Combine all the parts..."
              style="
                flex: 1; min-width: 200px; font-family: ${tokens.font.mono}; font-size: 1.4rem;
                padding: 14px 18px; border: 2px solid rgba(26, 54, 93, 0.2); border-radius: 12px;
                background: rgba(255, 253, 249, 0.95); color: ${tokens.color.morrisIndigo};
                letter-spacing: 0.15em; text-align: center; outline: none;
              "
            />
            <button id="btn-check-morpheme" class="touch-target btn-primary" style="padding: 14px 28px;">
              ✓ Assemble
            </button>
          </div>
        </div>

        <div id="morpheme-feedback" style="min-height: 60px; display: none;"></div>

        <div style="display: flex; justify-content: flex-end; margin-top: 16px;">
          <button id="btn-next-morpheme" class="touch-target btn-gold" style="padding: 10px 20px;">
            Next Word →
          </button>
        </div>
      </section>
    `;
  }

  /** Nessy visual mnemonic anchoring */
  private renderMnemonicCove(): string {
    const mnemonicWords = SPELLING_WORDS.filter(w => w.mnemonic);
    const word = mnemonicWords[this.currentWordIndex % mnemonicWords.length];

    if (!word) {
      return `<section class="morris-card" style="padding: 28px; text-align: center;">
        <p>No mnemonics available in this tier. Try The Reef or The Deep!</p>
      </section>`;
    }

    return `
      <section class="morris-card" style="padding: 28px 24px;">
        <div style="margin-bottom: 18px;">
          <h3 style="margin-bottom: 4px;">🧠 Mnemonic Cove</h3>
          <p style="font-size: 0.9rem; color: #6B7C93;">
            Lock tricky spellings into long-term memory with <strong>quirky visual stories</strong>!
          </p>
        </div>

        <!-- Mnemonic Card -->
        <div style="background: linear-gradient(135deg, rgba(255, 246, 229, 0.9), rgba(250, 246, 237, 0.95)); border-radius: 16px; padding: 36px 24px; text-align: center; margin-bottom: 20px; border: 2px solid ${tokens.color.gildedBrass};">
          <div style="font-size: 3.5rem; margin-bottom: 12px;">
            ${word.mnemonicEmoji || '💡'}
          </div>
          <div style="font-family: ${tokens.font.heading}; font-size: 2.2rem; font-weight: 700; color: ${tokens.color.morrisIndigo}; margin-bottom: 14px;">
            ${word.word}
          </div>
          <div style="font-size: 1.25rem; font-weight: 800; color: ${tokens.color.madderCoral}; line-height: 1.6; max-width: 520px; margin: 0 auto 16px;">
            "${word.mnemonic}"
          </div>
          ${word.rule ? `
            <div style="font-size: 0.85rem; color: #6B7C93; padding: 10px 16px; background: rgba(26, 54, 93, 0.05); border-radius: 8px; display: inline-block;">
              📘 Rule: ${word.rule}
            </div>
          ` : ''}
        </div>

        <!-- Practice recall -->
        <div style="margin-bottom: 18px;">
          <label style="display: block; font-size: 0.85rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; margin-bottom: 8px;">
            Recall the word using your memory trick:
          </label>
          <div style="display: flex; gap: 12px; flex-wrap: wrap;">
            <input
              type="text"
              id="mnemonic-input"
              autocomplete="off"
              spellcheck="false"
              placeholder="Type the word..."
              style="
                flex: 1; min-width: 200px; font-family: ${tokens.font.mono}; font-size: 1.4rem;
                padding: 14px 18px; border: 2px solid rgba(26, 54, 93, 0.2); border-radius: 12px;
                background: rgba(255, 253, 249, 0.95); color: ${tokens.color.morrisIndigo};
                letter-spacing: 0.15em; text-align: center; outline: none;
              "
            />
            <button id="btn-check-mnemonic" class="touch-target btn-primary" style="padding: 14px 28px;">
              ✓ Lock in Memory
            </button>
          </div>
        </div>

        <div id="mnemonic-feedback" style="min-height: 60px; display: none;"></div>

        <div style="display: flex; justify-content: flex-end; margin-top: 16px;">
          <button id="btn-next-mnemonic" class="touch-target btn-gold" style="padding: 10px 20px;">
            Next Mnemonic →
          </button>
        </div>
      </section>
    `;
  }

  /** Toe by Toe Error Word Review — spaced repetition of tricky words */
  private renderReefReview(): string {
    const errorWords = this.errorLog.filter(e => !e.correct);
    const uniqueErrorWords = Array.from(new Set(errorWords.map(e => e.word)));

    if (uniqueErrorWords.length === 0) {
      return `
        <section class="morris-card" style="padding: 40px 24px; text-align: center;">
          <span style="font-size: 3rem;">🎉</span>
          <h3 style="margin: 14px 0 8px; color: ${tokens.color.seaweedSage};">No Error Words Yet!</h3>
          <p style="color: #6B7C93; max-width: 480px; margin: 0 auto 20px;">
            Every word you've attempted has been spelled correctly. Head to Reef Diagnostic or Polynon Drill to challenge yourself!
          </p>
        </section>
      `;
    }

    return `
      <section class="morris-card" style="padding: 28px 24px;">
        <div style="margin-bottom: 18px;">
          <h3 style="margin-bottom: 4px;">🔄 Reef Review Deck</h3>
          <p style="font-size: 0.9rem; color: #6B7C93;">
            Toe by Toe rule: <strong>review error words three times</strong> before marking them mastered.
          </p>
        </div>

        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 14px;">
          ${uniqueErrorWords.map(w => {
            const wordObj = SPELLING_WORDS.find(sw => sw.word === w);
            const attempts = errorWords.filter(e => e.word === w).length;

            return `
              <div style="padding: 16px; border-radius: 12px; background: rgba(255, 107, 74, 0.06); border: 1.5px solid rgba(255, 107, 74, 0.2);">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                  <span style="font-family: ${tokens.font.heading}; font-size: 1.3rem; font-weight: 700; color: ${tokens.color.morrisIndigo};">
                    ${w}
                  </span>
                  <span style="font-size: 0.72rem; padding: 3px 8px; border-radius: 6px; background: rgba(255, 107, 74, 0.15); color: ${tokens.color.madderCoral}; font-weight: 800;">
                    ${attempts} attempt${attempts > 1 ? 's' : ''}
                  </span>
                </div>
                ${wordObj ? `
                  <div style="font-size: 0.8rem; color: #6B7C93; margin-bottom: 6px;">
                    Syllables: <strong style="color: ${tokens.color.morrisIndigo};">${wordObj.syllables.join(' · ')}</strong>
                  </div>
                  ${wordObj.mnemonic ? `
                    <div style="font-size: 0.78rem; color: ${tokens.color.seaweedSage}; font-style: italic;">
                      💡 ${wordObj.mnemonic}
                    </div>
                  ` : ''}
                ` : ''}
              </div>
            `;
          }).join('')}
        </div>
      </section>
    `;
  }

  // ─── Progress Tracker ────────────────────────────

  private renderProgressTracker(): string {
    const totalAttempts = this.errorLog.length;
    const correctAttempts = this.errorLog.filter(e => e.correct).length;
    const accuracy = totalAttempts > 0 ? Math.round((correctAttempts / totalAttempts) * 100) : 0;

    return `
      <div class="morris-card" style="margin-top: 20px; padding: 18px 20px;">
        <h4 style="margin-bottom: 14px; font-size: 1rem; color: ${tokens.color.morrisIndigo};">
          📈 Session Progress (Saved Locally)
        </h4>
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 14px;">
          <div style="text-align: center; padding: 12px; background: rgba(61, 90, 69, 0.06); border-radius: 10px;">
            <div style="font-size: 0.72rem; color: ${tokens.color.antiqueBrass}; font-weight: 800; text-transform: uppercase;">Words Attempted</div>
            <div style="font-family: ${tokens.font.mono}; font-size: 1.8rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">${totalAttempts}</div>
          </div>
          <div style="text-align: center; padding: 12px; background: rgba(61, 90, 69, 0.06); border-radius: 10px;">
            <div style="font-size: 0.72rem; color: ${tokens.color.antiqueBrass}; font-weight: 800; text-transform: uppercase;">Correct</div>
            <div style="font-family: ${tokens.font.mono}; font-size: 1.8rem; font-weight: 800; color: ${tokens.color.seaweedSage};">${correctAttempts}</div>
          </div>
          <div style="text-align: center; padding: 12px; background: rgba(61, 90, 69, 0.06); border-radius: 10px;">
            <div style="font-size: 0.72rem; color: ${tokens.color.antiqueBrass}; font-weight: 800; text-transform: uppercase;">Accuracy</div>
            <div style="font-family: ${tokens.font.mono}; font-size: 1.8rem; font-weight: 800; color: ${accuracy >= 70 ? tokens.color.seaweedSage : tokens.color.madderCoral};">${accuracy}%</div>
          </div>
          <div style="text-align: center; padding: 12px; background: rgba(61, 90, 69, 0.06); border-radius: 10px;">
            <div style="font-size: 0.72rem; color: ${tokens.color.antiqueBrass}; font-weight: 800; text-transform: uppercase;">Error Words</div>
            <div style="font-family: ${tokens.font.mono}; font-size: 1.8rem; font-weight: 800; color: ${tokens.color.madderCoral};">${this.errorLog.filter(e => !e.correct).length}</div>
          </div>
        </div>
      </div>
    `;
  }

  // ─── Methodology Info Panel ──────────────────────

  private renderMethodologyInfo(): string {
    return `
      <details class="morris-card" style="margin-top: 20px; padding: 18px 20px; cursor: pointer;">
        <summary style="font-size: 1rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; list-style: none; display: flex; align-items: center; gap: 8px;">
          <span>📚</span> About This Station's Spelling Science
          <span style="margin-left: auto; font-size: 0.8rem; color: ${tokens.color.antiqueBrass};">tap to expand</span>
        </summary>
        <div style="margin-top: 14px; display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px;">
          <div style="padding: 16px; background: rgba(255, 107, 74, 0.05); border-radius: 10px; border: 1px solid rgba(255, 107, 74, 0.12);">
            <h4 style="font-size: 0.95rem; color: ${tokens.color.madderCoral}; margin-bottom: 8px;">🐟 Nessy Principles</h4>
            <ul style="font-size: 0.85rem; color: ${tokens.color.morrisIndigo}; list-style: none; display: flex; flex-direction: column; gap: 6px;">
              <li>✦ <strong>Systematic Phonics:</strong> Sounds → letters in structured order</li>
              <li>✦ <strong>Syllable Chunking:</strong> Break big words into bite-sized pieces</li>
              <li>✦ <strong>Visual Mnemonics:</strong> Quirky stories that stick in long-term memory</li>
              <li>✦ <strong>Morphology:</strong> Prefixes, roots, and suffixes architecture</li>
              <li>✦ <strong>Diagnostic First:</strong> Find gaps before drilling</li>
              <li>✦ <strong>Cumulative:</strong> Master one tier before moving to the next</li>
            </ul>
          </div>
          <div style="padding: 16px; background: rgba(26, 54, 93, 0.04); border-radius: 10px; border: 1px solid rgba(26, 54, 93, 0.1);">
            <h4 style="font-size: 0.95rem; color: ${tokens.color.morrisIndigo}; margin-bottom: 8px;">🐡 Toe by Toe Principles</h4>
            <ul style="font-size: 0.85rem; color: ${tokens.color.morrisIndigo}; list-style: none; display: flex; flex-direction: column; gap: 6px;">
              <li>✦ <strong>Polynons:</strong> Nonsense words eliminate whole-word guessing</li>
              <li>✦ <strong>Micro-Steps:</strong> Tiny increments prevent cognitive overload</li>
              <li>✦ <strong>20-Minute Rule:</strong> Short, focused daily sessions</li>
              <li>✦ <strong>Error Tracking:</strong> Immediate feedback loops without negative framing</li>
              <li>✦ <strong>Multisensory:</strong> See, hear, and type simultaneously</li>
              <li>✦ <strong>Praise Progress:</strong> Celebrate every streak milestone</li>
            </ul>
          </div>
        </div>
      </details>
    `;
  }

  // ─── Helper Renderers ────────────────────────────

  private renderDiagnosticWordGrid(): string {
    return SPELLING_WORDS.map((w, i) => {
      const attempt = this.errorLog.find(e => e.word === w.word);
      let bgColor = 'rgba(26, 54, 93, 0.04)';
      let borderColor = 'rgba(26, 54, 93, 0.1)';
      let indicator = '○';

      if (attempt) {
        if (attempt.correct) {
          bgColor = 'rgba(61, 90, 69, 0.1)';
          borderColor = tokens.color.seaweedSage;
          indicator = '✓';
        } else {
          bgColor = 'rgba(255, 107, 74, 0.1)';
          borderColor = tokens.color.madderCoral;
          indicator = '✗';
        }
      }

      const isActive = i === this.currentWordIndex;

      return `
        <div style="
          padding: 8px 10px; border-radius: 8px; font-size: 0.82rem; text-align: center;
          background: ${isActive ? 'rgba(0, 229, 255, 0.1)' : bgColor};
          border: 1.5px solid ${isActive ? tokens.color.biolumCyan : borderColor};
          color: ${tokens.color.morrisIndigo}; font-weight: ${isActive ? 800 : 600};
          cursor: pointer; transition: all 0.15s ease;
        " data-word-index="${i}" class="diagnostic-word-cell">
          <span style="font-size: 0.7rem; margin-right: 4px;">${indicator}</span>
          ${w.word}
        </div>
      `;
    }).join('');
  }

  // ─── Utilities ───────────────────────────────────

  private getCurrentDiagnosticWord(): SpellingWord {
    return SPELLING_WORDS[this.currentWordIndex % SPELLING_WORDS.length]!;
  }

  private getTierColor(): string {
    switch (this.currentTier) {
      case 'shallows': return tokens.color.seaweedSage;
      case 'reef': return tokens.color.gildedBrass;
      case 'deep': return tokens.color.madderCoral;
      case 'abyss': return tokens.color.morrisIndigo;
    }
  }

  private getTierLabel(): string {
    switch (this.currentTier) {
      case 'shallows': return '🐚 The Shallows';
      case 'reef': return '🪸 The Reef';
      case 'deep': return '🐙 The Deep';
      case 'abyss': return '🌊 The Abyss';
    }
  }

  // ─── Event Wiring ────────────────────────────────

  private attachStaticListeners(container: HTMLElement): void {
    // Sound toggle (header)
    const toggleSoundBtn = container.querySelector<HTMLButtonElement>('#btn-toggle-sound');
    toggleSoundBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      const muted = soundEngine.toggleMute();
      toggleSoundBtn.textContent = muted ? '🔇 Off' : '🔊 Sound';
    });

    // Activity selector buttons
    container.querySelectorAll<HTMLButtonElement>('.spelling-activity-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        soundEngine.playTap();
        const activity = btn.dataset.activity as SpellingActivity;
        if (activity && activity !== this.currentActivity) {
          this.currentActivity = activity;
          this.currentWordIndex = 0;
          this.currentPolynon = 0;
          this.saveState();
          this.updateActivityButtonStyles(container);
          this.refreshStage(container);
        }
      });
    });

    // Session timer
    const startBtn = container.querySelector<HTMLButtonElement>('#btn-start-session');
    startBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      this.toggleSession(container);
    });
  }

  private updateActivityButtonStyles(container: HTMLElement): void {
    container.querySelectorAll<HTMLButtonElement>('.spelling-activity-btn').forEach(btn => {
      const isActive = btn.dataset.activity === this.currentActivity;
      btn.classList.toggle('active-activity', isActive);
    });
  }

  private attachStageListeners(container: HTMLElement): void {
    const stage = container.querySelector<HTMLElement>('#spelling-activity-stage');
    if (!stage) return;

    // Diagnostic: check spelling
    const checkBtn = stage.querySelector<HTMLButtonElement>('#btn-check-spelling');
    const spellingInput = stage.querySelector<HTMLInputElement>('#spelling-input');
    checkBtn?.addEventListener('click', () => {
      if (spellingInput) this.checkSpelling(spellingInput.value, container);
    });
    spellingInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && spellingInput) this.checkSpelling(spellingInput.value, container);
    });

    // Diagnostic: hear word (Web Audio + Human Speech Synthesis)
    const hearBtn = stage.querySelector<HTMLButtonElement>('#btn-hear-word');
    hearBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      const word = this.getCurrentDiagnosticWord();
      this.speakWord(word.word);
    });

    // Diagnostic: voice selector dynamic population & synchronization
    const voiceSelect = stage.querySelector<HTMLSelectElement>('#select-active-voice');
    const populateVoiceOptions = () => {
      if (!voiceSelect) return;
      const voices = SpeechManager.getAvailableVoices();
      if (!voices || voices.length === 0) return;

      const activeVoice = SpeechManager.resolveVoiceForPersona();
      const savedName = SpeechManager.getSelectedVoiceName() || activeVoice?.name;

      const englishVoices = voices.filter((v) => v.lang.toLowerCase().replace('_', '-').startsWith('en'));
      const list = englishVoices.length > 0 ? englishVoices : voices;
      const sorted = [...list].sort((a, b) => SpeechManager.scoreVoice(b) - SpeechManager.scoreVoice(a));

      voiceSelect.innerHTML = sorted
        .map((v) => {
          const isHighQuality = SpeechManager.scoreVoice(v) >= 100;
          const prefix = isHighQuality ? '✨ ' : '';
          const isSelected = v.name === savedName;
          return `<option value="${v.name}" ${isSelected ? 'selected' : ''}>${prefix}${v.name} (${v.lang})</option>`;
        })
        .join('');
    };

    populateVoiceOptions();
    SpeechManager.onVoicesChanged(() => {
      populateVoiceOptions();
    });

    voiceSelect?.addEventListener('change', () => {
      soundEngine.playTap();
      if (voiceSelect.value) {
        SpeechManager.setSelectedVoiceName(voiceSelect.value);
        SpeechManager.testPersona();
      }
    });

    // Diagnostic: test voice button
    const testVoiceBtn = stage.querySelector<HTMLButtonElement>('#btn-test-voice');
    testVoiceBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      SpeechManager.testPersona();
    });

    // Diagnostic: speech speed selector
    const speedSelect = stage.querySelector<HTMLSelectElement>('#select-voice-speed');
    speedSelect?.addEventListener('change', () => {
      const mult = parseFloat(speedSelect.value) || 0.92;
      SpeechManager.setSpeedMultiplier(mult);
      SpeechManager.testPersona();
    });

    // Diagnostic: navigation
    stage.querySelector('#btn-next-word')?.addEventListener('click', () => {
      soundEngine.playTap();
      this.currentWordIndex = Math.min(this.currentWordIndex + 1, SPELLING_WORDS.length - 1);
      this.refreshStage(container);
    });
    stage.querySelector('#btn-prev-word')?.addEventListener('click', () => {
      soundEngine.playTap();
      this.currentWordIndex = Math.max(this.currentWordIndex - 1, 0);
      this.refreshStage(container);
    });

    // Diagnostic: word grid clicks
    container.querySelectorAll<HTMLElement>('.diagnostic-word-cell').forEach(cell => {
      cell.onclick = () => {
        soundEngine.playTap();
        const idx = parseInt(cell.dataset.wordIndex ?? '0', 10);
        this.currentWordIndex = idx;
        this.refreshStage(container);
      };
    });

    // Polynon: check
    const checkPolynon = stage.querySelector<HTMLButtonElement>('#btn-check-polynon');
    const polynonInput = stage.querySelector<HTMLInputElement>('#polynon-input');
    checkPolynon?.addEventListener('click', () => {
      if (polynonInput) this.checkPolynon(polynonInput.value, container);
    });
    polynonInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && polynonInput) this.checkPolynon(polynonInput.value, container);
    });

    // Polynon: navigation
    stage.querySelector('#btn-next-polynon')?.addEventListener('click', () => {
      soundEngine.playTap();
      const tierPolynons = POLYNONS.filter(p => p.tier === this.currentTier);
      this.currentPolynon = Math.min(this.currentPolynon + 1, tierPolynons.length - 1);
      this.refreshStage(container);
    });
    stage.querySelector('#btn-prev-polynon')?.addEventListener('click', () => {
      soundEngine.playTap();
      this.currentPolynon = Math.max(this.currentPolynon - 1, 0);
      this.refreshStage(container);
    });

    // Syllable: check
    const checkSyllables = stage.querySelector<HTMLButtonElement>('#btn-check-syllables');
    checkSyllables?.addEventListener('click', () => this.checkSyllables(container));

    // Syllable: next
    stage.querySelector('#btn-next-syllable')?.addEventListener('click', () => {
      soundEngine.playTap();
      const multisyllabicWords = SPELLING_WORDS.filter(w => w.syllables.length >= 2);
      this.currentWordIndex = (this.currentWordIndex + 1) % multisyllabicWords.length;
      this.refreshStage(container);
    });

    // Morpheme: check
    const checkMorpheme = stage.querySelector<HTMLButtonElement>('#btn-check-morpheme');
    const morphemeInput = stage.querySelector<HTMLInputElement>('#morpheme-input');
    checkMorpheme?.addEventListener('click', () => {
      if (morphemeInput) this.checkMorpheme(morphemeInput.value, container);
    });
    morphemeInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && morphemeInput) this.checkMorpheme(morphemeInput.value, container);
    });

    // Morpheme: next
    stage.querySelector('#btn-next-morpheme')?.addEventListener('click', () => {
      soundEngine.playTap();
      const morphWords = SPELLING_WORDS.filter(w => w.morphemes && w.morphemes.length >= 2);
      this.currentWordIndex = (this.currentWordIndex + 1) % morphWords.length;
      this.refreshStage(container);
    });

    // Mnemonic: check
    const checkMnemonic = stage.querySelector<HTMLButtonElement>('#btn-check-mnemonic');
    const mnemonicInput = stage.querySelector<HTMLInputElement>('#mnemonic-input');
    checkMnemonic?.addEventListener('click', () => {
      if (mnemonicInput) this.checkMnemonic(mnemonicInput.value, container);
    });
    mnemonicInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && mnemonicInput) this.checkMnemonic(mnemonicInput.value, container);
    });

    // Mnemonic: next
    stage.querySelector('#btn-next-mnemonic')?.addEventListener('click', () => {
      soundEngine.playTap();
      const mnemonicWords = SPELLING_WORDS.filter(w => w.mnemonic);
      this.currentWordIndex = (this.currentWordIndex + 1) % mnemonicWords.length;
      this.refreshStage(container);
    });

    // Input focus styling inside stage
    stage.querySelectorAll<HTMLInputElement>('input[type="text"]').forEach(input => {
      input.addEventListener('focus', () => {
        input.style.borderColor = tokens.color.biolumCyan;
        input.style.boxShadow = `0 0 0 3px rgba(0, 229, 255, 0.15)`;
      });
      input.addEventListener('blur', () => {
        input.style.borderColor = 'rgba(26, 54, 93, 0.2)';
        input.style.boxShadow = 'none';
      });
    });
  }

  // ─── Checking Logic ──────────────────────────────

  private checkSpelling(attempt: string, container: HTMLElement): void {
    const word = this.getCurrentDiagnosticWord();
    const correct = attempt.trim().toLowerCase() === word.word.toLowerCase();

    this.logAttempt(word.word, attempt, correct, 'phoneme');
    this.updateStreak(correct);
    this.showFeedback(container, '#spelling-feedback', correct, word);

    // Audio & Mascot Feedback
    if (correct) {
      const tierMap: Record<SpellingTier, number> = { shallows: 1, reef: 4, deep: 7, abyss: 9 };
      const setNum = tierMap[this.currentTier] || 1;
      StationProgression.completeSet('spelling', setNum, Math.min(5, this.streakCount), 5);

      if (this.streakCount >= 3) {
        soundEngine.playStreak();
      } else {
        soundEngine.playSuccess();
      }
      this.triggerMascotPraise(word);
    } else {
      soundEngine.playTryAgain();
      this.triggerMascotHint(word);
    }
  }

  private checkPolynon(attempt: string, container: HTMLElement): void {
    const tierPolynons = POLYNONS.filter(p => p.tier === this.currentTier);
    const polynon = tierPolynons[this.currentPolynon % (tierPolynons.length || 1)];
    if (!polynon) return;

    const correct = attempt.trim().toLowerCase() === polynon.nonsenseWord.toLowerCase();

    this.logAttempt(polynon.nonsenseWord, attempt, correct, 'polynon');
    this.updateStreak(correct);

    if (correct) {
      soundEngine.playSuccess();
      if (this.hud) {
        this.hud.say(`Brilliant decoding! "${polynon.nonsenseWord}" sounded out perfectly without guessing!`, 'celebrating');
      }
    } else {
      soundEngine.playTryAgain();
      if (this.hud) {
        this.hud.say(`Sound it out phoneme by phoneme: ${polynon.phonemePattern}. You've got this!`, 'thinking');
      }
    }

    const feedbackEl = container.querySelector<HTMLElement>('#polynon-feedback');
    const bridgeEl = container.querySelector<HTMLElement>('#polynon-bridge');

    if (feedbackEl) {
      feedbackEl.style.display = 'block';
      feedbackEl.innerHTML = correct
        ? `<div style="display: flex; align-items: center; gap: 10px;">
             <span style="font-size: 1.8rem;">🎉</span>
             <div>
               <p style="font-weight: 800; color: ${tokens.color.seaweedSage}; font-size: 1.05rem;">Brilliant decoding!</p>
               <p style="font-size: 0.85rem; color: #6B7C93;">You sounded out "${polynon.nonsenseWord}" perfectly — no guessing!</p>
             </div>
           </div>`
        : `<div style="display: flex; align-items: center; gap: 10px;">
             <span style="font-size: 1.8rem;">🔄</span>
             <div>
               <p style="font-weight: 800; color: ${tokens.color.madderCoral}; font-size: 1.05rem;">Let's try again!</p>
               <p style="font-size: 0.85rem; color: #6B7C93;">
                 Sound it out slowly: <strong>${polynon.phonemePattern}</strong>
               </p>
             </div>
           </div>`;
    }

    if (bridgeEl && correct) {
      bridgeEl.style.display = 'block';
    }
  }

  private checkSyllables(container: HTMLElement): void {
    const multisyllabicWords = SPELLING_WORDS.filter(w => w.syllables.length >= 2);
    const word = multisyllabicWords[this.currentWordIndex % multisyllabicWords.length];
    if (!word) return;

    const slots = container.querySelectorAll<HTMLInputElement>('.syllable-slot');
    const userChunks = Array.from(slots).map(s => s.value.trim().toLowerCase());
    const correct = word.syllables.every((s, i) => userChunks[i] === s.toLowerCase());

    this.logAttempt(word.word, userChunks.join('-'), correct, 'syllable');
    this.updateStreak(correct);

    // Audio & Mascot Feedback
    if (correct) {
      soundEngine.playWaterSplash();
      soundEngine.playSuccess();
      if (this.hud) {
        this.hud.say(`Splendid clapping! "${word.word}" splits cleanly into: ${word.syllables.join(' · ')}!`, 'celebrating');
      }
    } else {
      soundEngine.playTryAgain();
      if (this.hud) {
        this.hud.say(`Clap the rhythm out loud, Tudor: it has ${word.syllables.length} distinct beats!`, 'thinking');
      }
    }

    // Color individual slots
    slots.forEach((slot, i) => {
      const isRight = userChunks[i] === word.syllables[i]?.toLowerCase();
      slot.style.borderColor = isRight ? tokens.color.seaweedSage : tokens.color.madderCoral;
      slot.style.background = isRight ? 'rgba(61, 90, 69, 0.08)' : 'rgba(255, 107, 74, 0.08)';
    });

    const feedbackEl = container.querySelector<HTMLElement>('#syllable-feedback');
    if (feedbackEl) {
      feedbackEl.style.display = 'block';
      feedbackEl.innerHTML = correct
        ? `<div class="morris-card" style="padding: 14px 18px; border-left: 4px solid ${tokens.color.seaweedSage};">
             <p style="font-weight: 800; color: ${tokens.color.seaweedSage};">🌊 Perfect syllable split!</p>
             <p style="font-size: 0.9rem; color: #6B7C93;">${word.word} → ${word.syllables.join(' · ')}</p>
           </div>`
        : `<div class="morris-card" style="padding: 14px 18px; border-left: 4px solid ${tokens.color.madderCoral};">
             <p style="font-weight: 800; color: ${tokens.color.madderCoral};">Almost! Try splitting differently.</p>
             <p style="font-size: 0.9rem; color: #6B7C93;">Hint: This word has ${word.syllables.length} syllable${word.syllables.length > 1 ? 's' : ''}. Clap them out!</p>
           </div>`;
    }

    // Show rule on correct
    const ruleEl = container.querySelector<HTMLElement>('#syllable-rule');
    if (ruleEl && correct) {
      ruleEl.style.display = 'block';
    }
  }

  private checkMorpheme(attempt: string, container: HTMLElement): void {
    const morphWords = SPELLING_WORDS.filter(w => w.morphemes && w.morphemes.length >= 2);
    const word = morphWords[this.currentWordIndex % morphWords.length];
    if (!word) return;

    const correct = attempt.trim().toLowerCase() === word.word.toLowerCase();
    this.logAttempt(word.word, attempt, correct, 'morpheme');
    this.updateStreak(correct);
    this.showFeedback(container, '#morpheme-feedback', correct, word);

    if (correct) {
      soundEngine.playSuccess();
      if (this.hud) {
        this.hud.say(`Morphemes locked together! You reconstructed "${word.word}" from its roots!`, 'celebrating');
      }
    } else {
      soundEngine.playTryAgain();
      if (this.hud) {
        this.hud.say(`Look closely at the joins: observe where prefix and root meet!`, 'thinking');
      }
    }
  }

  private checkMnemonic(attempt: string, container: HTMLElement): void {
    const mnemonicWords = SPELLING_WORDS.filter(w => w.mnemonic);
    const word = mnemonicWords[this.currentWordIndex % mnemonicWords.length];
    if (!word) return;

    const correct = attempt.trim().toLowerCase() === word.word.toLowerCase();
    this.logAttempt(word.word, attempt, correct, 'phoneme');
    this.updateStreak(correct);
    this.showFeedback(container, '#mnemonic-feedback', correct, word);

    if (correct) {
      soundEngine.playSuccess();
      if (this.hud) {
        this.hud.say(`Memory trick anchored! "${word.word}" is safe in your naturalist log!`, 'celebrating');
      }
    } else {
      soundEngine.playTryAgain();
      if (this.hud && word.mnemonic) {
        this.hud.say(`Recall the trick: "${word.mnemonic}". Give it another spin!`, 'thinking');
      }
    }
  }

  // ─── Socratic Mascot Reactions ───────────────────

  private triggerMascotPraise(word: SpellingWord): void {
    if (!this.hud) return;

    if (this.streakCount === 3) {
      this.hud.say(`Three in a row! The tide is turning in your favour, Tudor!`, 'celebrating');
      return;
    }
    if (this.streakCount === 5) {
      this.hud.say(`Five consecutive pearls! Master Naturalist in the making!`, 'celebrating');
      return;
    }
    if (this.streakCount === 10) {
      this.hud.say(`By Neptune's golden trident! TEN in a row! Extraordinary!`, 'celebrating');
      return;
    }

    const praises = [
      `Splendid spelling, Tudor! "${word.word}" is mastered!`,
      `By the claws of Neptune! Outstanding work on "${word.word}"!`,
      `Sharp eyes! You navigated the tricky letters with true precision!`,
      `A magnificent catch! That's another word charted for our expedition!`,
    ];
    const praise = praises[Math.floor(Math.random() * praises.length)]!;
    this.hud.say(praise, 'celebrating');
  }

  private triggerMascotHint(word: SpellingWord): void {
    if (!this.hud) return;

    if (word.mnemonic) {
      this.hud.say(`An epic try! Remember Clawey's clue: "${word.mnemonic}". Have another go!`, 'thinking');
    } else if (word.rule) {
      this.hud.say(`Close try! Look at the rule: ${word.rule}. You can decode this!`, 'thinking');
    } else if (word.syllables.length > 1) {
      this.hud.say(`Clap the syllables: ${word.syllables.join(' · ')}. Which chunk needs attention?`, 'thinking');
    } else {
      this.hud.say(`An honourable attempt! Sound out each phoneme slowly: ${word.phonemes.join('-')}.`, 'thinking');
    }
  }

  // ─── Shared Feedback Renderer ────────────────────

  private showFeedback(container: HTMLElement, selector: string, correct: boolean, word: SpellingWord): void {
    const feedbackEl = container.querySelector<HTMLElement>(selector);
    if (!feedbackEl) return;

    feedbackEl.style.display = 'block';

    if (correct) {
      feedbackEl.innerHTML = `
        <div style="display: flex; align-items: center; gap: 12px; padding: 14px 18px; background: rgba(61, 90, 69, 0.08); border-radius: 12px; border-left: 4px solid ${tokens.color.seaweedSage};">
          <span style="font-size: 2rem;">🎉</span>
          <div>
            <p style="font-weight: 800; color: ${tokens.color.seaweedSage}; font-size: 1.05rem;">
              Splendid spelling, Tudor!
            </p>
            <p style="font-size: 0.85rem; color: #6B7C93;">
              "${word.word}" — perfectly decoded! Streak: ${this.streakCount} 🔥
            </p>
          </div>
        </div>
      `;
    } else {
      feedbackEl.innerHTML = `
        <div style="display: flex; align-items: flex-start; gap: 12px; padding: 14px 18px; background: rgba(255, 107, 74, 0.06); border-radius: 12px; border-left: 4px solid ${tokens.color.madderCoral};">
          <span style="font-size: 2rem;">🔄</span>
          <div>
            <p style="font-weight: 800; color: ${tokens.color.madderCoral}; font-size: 1.05rem;">
              An epic try! Let's decode it together.
            </p>
            <p style="font-size: 0.9rem; color: ${tokens.color.morrisIndigo}; margin-top: 6px;">
              <strong>Phonemes:</strong> ${word.phonemes.map(p => `<span style="padding: 2px 6px; background: rgba(0,229,255,0.08); border-radius: 4px; font-family: ${tokens.font.mono};">${p}</span>`).join(' ')}
            </p>
            <p style="font-size: 0.9rem; color: ${tokens.color.morrisIndigo}; margin-top: 4px;">
              <strong>Syllables:</strong> ${word.syllables.join(' · ')}
            </p>
            ${word.mnemonic ? `
              <p style="font-size: 0.9rem; color: ${tokens.color.seaweedSage}; margin-top: 4px; font-style: italic;">
                💡 <strong>Memory trick:</strong> ${word.mnemonic}
              </p>
            ` : ''}
            ${word.rule ? `
              <p style="font-size: 0.85rem; color: #6B7C93; margin-top: 4px;">
                📘 ${word.rule}
              </p>
            ` : ''}
          </div>
        </div>
      `;
    }
  }

  // ─── Session Timer Logic ─────────────────────────

  private toggleSession(container: HTMLElement): void {
    if (this.sessionActive) {
      this.pauseSession(container);
    } else {
      this.startSession(container);
    }
  }

  private startSession(container: HTMLElement): void {
    this.sessionActive = true;
    this.sessionStartTime = Date.now();

    const btn = container.querySelector<HTMLButtonElement>('#btn-start-session');
    if (btn) btn.textContent = '⏸ Pause';

    this.sessionTimerInterval = setInterval(() => {
      const elapsed = Date.now() - this.sessionStartTime;
      const remaining = Math.max(0, SESSION_DURATION_MS - elapsed);
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);

      const display = container.querySelector<HTMLElement>('#timer-display');
      if (display) {
        display.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        if (remaining < 5 * 60 * 1000) {
          display.style.color = tokens.color.madderCoral;
        }
      }

      const progress = container.querySelector<HTMLElement>('#timer-progress');
      if (progress) {
        progress.style.width = `${(elapsed / SESSION_DURATION_MS) * 100}%`;
      }

      if (remaining <= 0) {
        this.endSession(container);
      }
    }, 1000);
  }

  private pauseSession(container: HTMLElement): void {
    this.sessionActive = false;
    if (this.sessionTimerInterval) {
      clearInterval(this.sessionTimerInterval);
      this.sessionTimerInterval = null;
    }

    const btn = container.querySelector<HTMLButtonElement>('#btn-start-session');
    if (btn) btn.textContent = '▶ Resume';
  }

  private endSession(container: HTMLElement): void {
    this.sessionActive = false;
    if (this.sessionTimerInterval) {
      clearInterval(this.sessionTimerInterval);
      this.sessionTimerInterval = null;
    }

    soundEngine.playSessionComplete();
    if (this.hud) {
      this.hud.say(`Expedition complete! 20 minutes of supreme naturalist focus, Tudor! Well done!`, 'celebrating');
    }

    const display = container.querySelector<HTMLElement>('#timer-display');
    if (display) {
      display.textContent = '00:00';
      display.style.color = tokens.color.seaweedSage;
    }

    const btn = container.querySelector<HTMLButtonElement>('#btn-start-session');
    if (btn) {
      btn.textContent = '🎉 Session Complete!';
      btn.disabled = true;
      btn.style.opacity = '0.7';
    }
  }

  // ─── Logging & Streak ────────────────────────────

  private logAttempt(word: string, attempt: string, correct: boolean, errorType: SpellingAttempt['errorType']): void {
    const entry: SpellingAttempt = {
      word,
      attempt: attempt.trim().toLowerCase(),
      correct,
      timestamp: Date.now(),
      errorType,
    };
    this.errorLog.push(entry);
    SpellingStorage.recordAttempt(entry);
  }

  private updateStreak(correct: boolean): void {
    if (correct) {
      this.streakCount++;
      if (this.streakCount > this.bestStreak) {
        this.bestStreak = this.streakCount;
      }
    } else {
      this.streakCount = 0;
    }
    this.saveState();
  }

  private saveState(): void {
    SpellingStorage.save({
      errorLog: this.errorLog,
      streakCount: this.streakCount,
      bestStreak: this.bestStreak,
      masteredWords: this.errorLog.filter(e => e.correct).map(e => e.word.toLowerCase()),
      currentTier: this.currentTier,
      currentActivity: this.currentActivity,
      totalTimeMs: 0,
      lastSessionDate: new Date().toISOString().split('T')[0] ?? '',
    });
  }

  /** Use SpeechManager human voice engine for multisensory auditory channel */
  private speakWord(word: string): void {
    SpeechManager.speak(word);
  }

  // ─── Refresh ─────────────────────────────────────

  private refreshStage(container: HTMLElement): void {
    const stage = container.querySelector<HTMLElement>('#spelling-activity-stage');
    if (stage) {
      stage.innerHTML = this.renderCurrentActivity();
    }
    // Update streak counter in the header
    const streakDisplay = container.querySelector<HTMLElement>('#spelling-streak');
    if (streakDisplay) {
      streakDisplay.textContent = String(this.streakCount);
    }
    // Re-attach stage listeners ONLY for the new stage elements
    this.attachStageListeners(container);
  }
}
